import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { useTranslation } from 'react-i18next'

import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import { sideLabel } from '~/components/bidding/common/sideLabel'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { convertAreaLabel } from '~/models/area'
import { isIntradayMarketOrder } from '~/models/intradayOrderMethod'
import { formatKomaCompactLabel } from '~/models/komaLabel'
import {
  formatBidPrice,
  formatBidVolumeMw,
} from '~/pages/IntradayBidding/models/intradayBidFigures'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'

/** 「詳細を見る」 の 1 行に並べる札の項目 (コマ・エリア・発注主体区分・売/買・入札価格・入札量)。 */
export type IntradayFailedBid = Pick<
  IntradayBidRow,
  | 'id'
  | 'timeCode'
  | 'areaCode'
  | 'direction'
  | 'priceYenPerKwh'
  | 'volumeMw'
  | 'orderMethod'
  | 'issuerType'
>

export type IntradayFailedBidRow = {
  bid: IntradayFailedBid
  /** 札ごとの理由 (表示する文言)。 */
  reason: string
}

const LICENSE_TYPE_OF_ISSUER = {
  generation: LicenseType.GENERATION,
  retail: LicenseType.RETAIL,
  selfwheeling: LicenseType.SELF_WHEELING,
} as const

const issuerLabelOf = (bid: IntradayFailedBid): string => {
  const licenseType =
    bid.issuerType === 'generation' ||
    bid.issuerType === 'retail' ||
    bid.issuerType === 'selfwheeling'
      ? LICENSE_TYPE_OF_ISSUER[bid.issuerType]
      : undefined
  return licenseType === undefined ? '—' : issuerTypeLabel(licenseType)
}

/**
 * まとめて行った操作が通らなかった札を、 理由とともに 1 札 1 行で並べる表 (提出 AC-BIN-009・
 * 入札を全削除 AC-BIN-138 の「詳細を見る」)。 成行の札は入札価格の代わりに「成行」を出す。
 */
export const IntradayFailedBidsTable = ({
  rows,
}: {
  rows: readonly IntradayFailedBidRow[]
}): React.ReactElement => {
  const { t } = useTranslation()
  const columns: Array<MasterDataTableColumn<IntradayFailedBidRow>> = [
    {
      key: 'timeCode',
      header: t('page:biddingIntraday.column.timeCode'),
      cell: (row) => formatKomaCompactLabel(t, row.bid.timeCode),
    },
    {
      key: 'area',
      header: t('page:biddingIntraday.column.area'),
      cell: (row) => convertAreaLabel(row.bid.areaCode),
    },
    {
      key: 'issuerType',
      header: t('page:biddingIntraday.bulkResult.columnIssuerType'),
      cell: (row) => issuerLabelOf(row.bid),
    },
    {
      key: 'side',
      header: t('page:biddingIntraday.column.side'),
      cell: (row) => sideLabel(t, row.bid.direction),
    },
    {
      key: 'price',
      header: t('page:biddingIntraday.form.priceYenPerKwh'),
      format: 'number',
      cell: (row) =>
        isIntradayMarketOrder(row.bid)
          ? t('page:biddingIntraday.bidTable.marketOrderPrice')
          : formatBidPrice(row.bid.priceYenPerKwh),
    },
    {
      key: 'volume',
      header: t('page:biddingIntraday.form.volumeMwh'),
      format: 'number',
      cell: (row) => formatBidVolumeMw(row.bid.volumeMw),
    },
    {
      key: 'reason',
      header: t('page:biddingIntraday.bulkResult.columnReason'),
      cell: (row) => row.reason,
    },
  ]
  return (
    <MasterDataTable
      columns={columns}
      getRowKey={(row) => row.bid.id}
      rows={[...rows]}
    />
  )
}
